// Imports for external libraries go here.
import { FC, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link, TrackingPropsInterface } from '@marriott/mi-ui-library';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import DOMPurify from 'isomorphic-dompurify';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { LanguageSelector } from '../Navigation/LanguageSelector';
import { LanguageSelectorContext } from '../Navigation/LanguageSelector/LanguageSelectorContextProvider';
import { StyledContainer, StyledFooterContainer, StyledP, LanguageSelectorContainer } from './Footer.styles';
import { socialLinks } from './Footer.types';
import { getClickTrackValue, DEFAULT_TRACKING_VALUES } from '../../utils';
import { initializeFooterId } from './helper';
import { FooterProps } from './Footer.types';
import { ExpandedFooter } from './ExpandedFooter';
import { CollapsedFooter } from './CollapsedFooter';
import { useDynamicWrapperContext } from '../../organisms/DynamicWrapper';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _satellite: Record<string, any>;
  }
}

// Use named rather than default exports.
export const FooterComp: FC<FooterProps> = (props: FooterProps) => {
  const { model } = props;

  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState<boolean>(false);

  const trackingPropertiesObject: {
    trackingProperties: TrackingPropsInterface;
    fallbacks: { position: string; description: string };
  } = {
    trackingProperties: {
      trackingContentPosition: model?.trackingProperties?.trackingContentPosition,
      clickTrack: model?.trackingProperties?.clickTrack,
      trackingOfferType: model?.trackingProperties?.trackingOfferType,
      impressionCount: model?.trackingProperties?.impressionCount,
      impressionTrack: model?.trackingProperties?.impressionTrack,
      trackingDescription: model?.trackingProperties?.trackingDescription,
      trackingTag: model?.trackingProperties?.trackingTag,
    },
    fallbacks: {
      position: model?.trackingProperties?.location ?? DEFAULT_TRACKING_VALUES.LABEL_NULLISH,
      description: model?.trackingProperties?.trackingDescription ?? DEFAULT_TRACKING_VALUES.LABEL_NULLISH,
    },
  };

  useEffect(() => {
    // we need to fire OneTrust once Footer is mounted, but it still may not be available on the window
    // so poll for it and then fire once it's available
    let attempts = 0;
    const checkWindow = () => {
      if (window?._satellite?.['track']) {
        window._satellite['track']('fireOneTrust');
      } else if (attempts < 30) {
        attempts++;
        setTimeout(checkWindow, 100);
      }
    };
    window.addEventListener('load', () => initializeFooterId());
    checkWindow();

    return () => {
      window.removeEventListener('load', () => initializeFooterId());
    };
  }, []);

  return (
    <LanguageSelectorContext.Provider value={{ isLanguageSelectorOpen, setIsLanguageSelectorOpen }}>
      <StyledContainer className="container">
        {model?.resourceProperties?.variation === 'expanded' ? (
          <ExpandedFooter model={model} />
        ) : (
          <CollapsedFooter model={model} />
        )}
        <div className="m-accordion-container inverse">
          {model?.socialItems?.length >= 1 && (
            <div className="accordion-item">
              <div className="accordion-header" id="headingOne">
                <div className="accordion-button pb-md-2">
                  <div className="m-accordion-title flex-column flex-md-row">
                    <StyledP className="t-font-l mr-4 mb-3">{model?.resourceProperties.socialTitle}</StyledP>
                    <div className="d-flex">
                      {model?.socialItems?.map((socialItem, index) => {
                        return (
                          <div className="mr-5" key={socialItem.linkLabel}>
                            <Link
                              linkClassName="t-font-alt-md"
                              ariaLabel={socialLinks[index]}
                              linkHref={socialItem.linkURL as string}
                              custom_click_track_value={getClickTrackValue(
                                trackingPropertiesObject,
                                socialItem.linkLabel
                              )}
                              trackingProperties={trackingPropertiesObject.trackingProperties}
                            >
                              <span className={`icon-${socialLinks[index]} icon-inverse`} />
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div
            className={clsx(
              'accordion-button d-flex flex-column flex-md-row align-items-start justify-content-around add-border',
              { 'is-overlay': isLanguageSelectorOpen }
            )}
          >
            <div
              className="m-accordion-title t-font-alt-xs align-self-center m-0"
              dangerouslySetInnerHTML={{
                __html: (
                  DOMPurify.sanitize(model?.resourceProperties?.copyrightDescription as string, {
                    RETURN_DOM: true,
                  }) as HTMLElement
                ).outerHTML,
              }}
            ></div>
            {!model?.hideGlobeIcon && (
              <LanguageSelectorContainer>
                <LanguageSelector
                  buttonClassName="footer-language-selector"
                  renderUtilityLink={() => (
                    <div className="d-flex flex-row align-items-center mt-4 mt-md-0 inverse">
                      <span className="icon-globe icon-inverse mr-1" />{' '}
                      <p className="t-font-alt-xs m-0">{model?.resourceProperties?.languageLable}</p>
                    </div>
                  )}
                  restProps={{
                    trackingContentPosition: model?.trackingProperties?.trackingContentPosition,
                    'cq:panelTitle': model?.resourceProperties?.languageLable,
                    resourceProperties: model?.resourceProperties,
                    trackingProperties: trackingPropertiesObject?.trackingProperties,
                    regions: model?.regions,
                  }}
                />
              </LanguageSelectorContainer>
            )}
          </div>
          <div className="row">
            <div className="js-session-detail t-label-inverse-xs" aria-hidden="true"></div>
          </div>
        </div>
      </StyledContainer>
    </LanguageSelectorContext.Provider>
  );
};

/* eslint-disable  @typescript-eslint/no-explicit-any */
export const Footer = (props: any) => {
  const FooterConfig = {
    emptyLabel: 'Footer',
    isEmpty: () => true,
    resourceType: `mi-aem-homepage-spa/components/content/universal-footer`,
    id: '',
  };

  const { isInsideDynamicWrapper } = useDynamicWrapperContext();

  const footerContent = (
    <StyledFooterContainer className="m-container-fullbleed" isBulgari={props?.isBulgari ?? false}>
      <FooterComp {...props} />
    </StyledFooterContainer>
  );

  const skipEditable = props.cqPath?.includes('experiencefragment') || isInsideDynamicWrapper;

  return (
    <div id="universal-footer" data-component-name="m-common-static-Footer" data-testid="common-static-Footer">
      {skipEditable ? (
        footerContent
      ) : (
        <EditableComponent {...props} config={FooterConfig}>
          {footerContent}
        </EditableComponent>
      )}
    </div>
  );
};
